/**
 * Portfolio File Uploader
 * TASK-210: Upload Excel/CSV/PDF files with securities positions
 * TASK-220: Refactored to use real extraction API (no mock data)
 *
 * Allows users to upload portfolio statements from banks
 * Extracts position data using AI-powered document analysis
 */

import React, { useState, useCallback } from 'react';
import {
  X,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Download,
  FileText,
  Loader2,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useDocumentUpload,
  isPortfolioData,
  PortfolioPosition,
  CashPosition
} from '../../hooks/useDocumentUpload';

// Position type for display (compatible with existing UI)
interface DisplayPosition {
  id: string;
  isin?: string;
  securityName: string;
  quantity: number;
  marketPrice: number;
  marketValue: number;
  currency: string;
  assetClass?: string;
  sector?: string;
  unrealizedPnL?: number;
  unrealizedPnLPercent?: number;
}

// Cash position type for display
interface DisplayCashPosition {
  currency: string;
  amount: number;
  accountType: 'Current' | 'Savings' | 'Investment';
}

interface PortfolioFileUploaderProps {
  isOpen: boolean;
  portfolioId: string;
  portfolioName: string;
  clientId: string;  // TASK-220: Required for API call
  onClose: () => void;
  onSuccess: (positions: DisplayPosition[], cashPositions?: DisplayCashPosition[]) => void;
}

type UploadStage = 'upload' | 'parsing' | 'review' | 'success';

const PortfolioFileUploader: React.FC<PortfolioFileUploaderProps> = ({
  isOpen,
  portfolioId,  // TASK-278: Use portfolioId for database persistence
  portfolioName,
  clientId,
  onClose,
  onSuccess
}) => {
  const [stage, setStage] = useState<UploadStage>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [extractedPositions, setExtractedPositions] = useState<DisplayPosition[]>([]);
  const [extractedCash, setExtractedCash] = useState<DisplayCashPosition[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [detectedBank, setDetectedBank] = useState<string | null>(null);
  const [confidenceScore, setConfidenceScore] = useState<number>(0);

  // TASK-278: Use the shared document upload hook - now persists to database
  const {
    upload,
    isUploading,
    isExtracting,
    progress,
    error,
    reset: resetUpload
  } = useDocumentUpload({
    clientId,
    portfolioId,  // TASK-278: Pass portfolioId for database persistence
    extractionType: 'portfolio',
    skipDatabase: false,  // TASK-278: Enable database persistence
    onProgress: (_prog) => {
      // Progress is handled by the hook
    },
    onExtracted: (data) => {
      if (isPortfolioData(data)) {
        // Convert API response to display format
        const displayPositions = mapToDisplayPositions(data.positions);
        const displayCash = mapToDisplayCash(data.cash_positions);

        setExtractedPositions(displayPositions);
        setExtractedCash(displayCash);
        setWarnings(data.warnings || []);
        setDetectedBank(data.detected_bank || null);
        setConfidenceScore(data.confidence_score);
        setStage('review');
      }
    },
    onError: (err) => {
      toast.error(err);
      setStage('upload');
    }
  });

  // Map API positions to display format
  const mapToDisplayPositions = (positions: PortfolioPosition[]): DisplayPosition[] => {
    return positions.map((pos, idx) => ({
      id: `POS-${Date.now()}-${idx}`,
      isin: pos.isin,
      securityName: pos.security_name || 'Unknown Security',
      quantity: pos.quantity || 0,
      marketPrice: pos.price || 0,
      marketValue: pos.market_value || (pos.quantity || 0) * (pos.price || 0),
      currency: pos.currency || 'CHF',
      assetClass: pos.asset_class,
      sector: undefined,  // Not provided by API
      unrealizedPnL: undefined,  // Would need cost basis
      unrealizedPnLPercent: undefined
    }));
  };

  // Map API cash positions to display format
  const mapToDisplayCash = (cashPositions: CashPosition[]): DisplayCashPosition[] => {
    return cashPositions.map(cash => ({
      currency: cash.currency,
      amount: cash.amount,
      accountType: (cash.account_type as 'Current' | 'Savings' | 'Investment') || 'Current'
    }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
        'application/pdf'
      ];

      if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx?|csv|pdf)$/i)) {
        toast.error('Please upload an Excel (.xlsx, .xls), CSV, or PDF file');
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleParse = useCallback(async () => {
    if (!selectedFile) return;

    setStage('parsing');
    setWarnings([]);

    // TASK-220: Call real extraction API via hook
    await upload(selectedFile);
  }, [selectedFile, upload]);

  const handleConfirm = () => {
    onSuccess(extractedPositions, extractedCash);
    toast.success(`Successfully imported ${extractedPositions.length} positions`);
    handleClose();
  };

  const handleClose = () => {
    setStage('upload');
    setSelectedFile(null);
    setExtractedPositions([]);
    setExtractedCash([]);
    setWarnings([]);
    setDetectedBank(null);
    setConfidenceScore(0);
    resetUpload();
    onClose();
  };

  const formatCurrency = (value: number, currency: string = 'CHF') => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (!isOpen) return null;

  const isProcessing = isUploading || isExtracting;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Upload Portfolio File</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Portfolio: {portfolioName}
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Stage 1: Upload */}
          {stage === 'upload' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <FileSpreadsheet className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Upload Portfolio Statement
                </h3>
                <p className="text-sm text-gray-600 max-w-md mx-auto">
                  Upload an Excel, CSV, or PDF file containing your portfolio positions from your bank or custodian
                </p>
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-900">Upload Failed</p>
                      <p className="text-xs text-red-700 mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* File Upload Area */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 hover:bg-blue-50 transition-all">
                <input
                  type="file"
                  id="portfolio-file-upload"
                  onChange={handleFileSelect}
                  accept=".xlsx,.xls,.csv,.pdf"
                  className="hidden"
                />
                <label
                  htmlFor="portfolio-file-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Upload className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">
                    Excel (.xlsx, .xls), CSV, or PDF (max 50MB)
                  </p>
                </label>
              </div>

              {selectedFile && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              )}

              {/* Supported Formats */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-gray-600">
                    <p className="font-medium text-gray-900 mb-2">Supported Formats</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Bank statements (PDF) - AI-powered position extraction</li>
                      <li>Portfolio export files (Excel/CSV) from UBS, Credit Suisse, Julius Baer, etc.</li>
                      <li>Custom Excel templates - Must include columns: ISIN, Name, Quantity, Price, Currency</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Download Template */}
              <div className="flex items-center justify-center">
                <button
                  onClick={() => toast.info('Template download coming soon!')}
                  className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  <Download className="w-4 h-4" />
                  Download Excel Template
                </button>
              </div>
            </div>
          )}

          {/* Stage 2: Parsing */}
          {stage === 'parsing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {isUploading ? 'Uploading File...' : 'Extracting Positions...'}
              </h3>
              <p className="text-sm text-gray-600 mb-6 text-center max-w-md">
                {isUploading
                  ? 'Uploading your file to the server...'
                  : 'AI is analyzing your document and extracting position data. This may take a moment.'}
              </p>

              {/* Progress Bar */}
              <div className="w-full max-w-md">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>{isUploading ? 'Uploading' : 'Extracting'}</span>
                  <span>{progress}%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Stage 3: Review */}
          {stage === 'review' && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-900 mb-1">
                      File Parsed Successfully
                    </p>
                    <p className="text-xs text-green-700">
                      Found {extractedPositions.length} securities positions and {extractedCash.length} cash accounts
                      {detectedBank && ` (Detected: ${detectedBank})`}
                      {confidenceScore > 0 && ` - Confidence: ${(confidenceScore * 100).toFixed(0)}%`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Warnings/Errors */}
              {warnings.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-yellow-900 mb-2">
                        Warnings
                      </p>
                      <ul className="space-y-1">
                        {warnings.map((warning, idx) => (
                          <li key={idx} className="text-xs text-yellow-700">
                            {warning}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Extracted Positions */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">
                  Securities Positions ({extractedPositions.length})
                </h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Security</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ISIN</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {extractedPositions.map((position) => (
                          <tr key={position.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{position.securityName}</td>
                            <td className="px-4 py-3 text-sm text-gray-500 font-mono">{position.isin || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                              {position.quantity.toLocaleString('de-CH')}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                              {formatCurrency(position.marketPrice, position.currency)}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                              {formatCurrency(position.marketValue, position.currency)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {position.assetClass || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50 font-semibold">
                        <tr>
                          <td colSpan={4} className="px-4 py-3 text-sm text-gray-900">Total Securities</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">
                            {formatCurrency(
                              extractedPositions.reduce((sum, p) => sum + p.marketValue, 0),
                              'CHF'
                            )}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>

              {/* Cash Positions */}
              {extractedCash.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Cash Positions ({extractedCash.length})
                  </h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Type</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {extractedCash.map((cash, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{cash.currency}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{cash.accountType}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                              {formatCurrency(cash.amount, cash.currency)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Note about preview mode */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Preview Mode</p>
                    <p className="text-xs text-blue-700 mt-1">
                      These positions will be added to your portfolio view. They are not yet saved to the database.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>

          <div className="flex items-center gap-3">
            {stage === 'upload' && (
              <button
                onClick={handleParse}
                disabled={!selectedFile || isProcessing}
                className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Parse File
                <ArrowRight className="w-4 h-4" />
              </button>
            )}

            {stage === 'review' && (
              <button
                onClick={handleConfirm}
                className="inline-flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <CheckCircle2 className="w-4 h-4" />
                Import {extractedPositions.length} Positions
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioFileUploader;
